import { useState } from 'react'
import { Plus } from 'lucide-react'
import Card from '@/components/layout/Card'
import { TaskRow } from '@/features/tasks/FocusTasks'
import NewTaskModal from '@/features/tasks/NewTaskModal'
import { useCompleteTask, useContactTasks } from '@/features/tasks/useTasks'
import { byDueDate, isTaskOverdue } from '@/lib/dealFormat'
import type { Contact } from '@/api/types'

/**
 * A contact's tasks — "call Sarah back" doesn't need a deal. Open ones first with a tick to complete,
 * plus "Add task"; tasks on this person's deals show here too (they carry the deal's main contact).
 */
export default function ContactTasksCard({ contact, adding, onAddingChange }: {
  contact: Contact
  /** Lifted so the header's "Task" button can open the same dialog. */
  adding: boolean
  onAddingChange: (open: boolean) => void
}) {
  const { data: tasks } = useContactTasks(contact.id)
  const complete = useCompleteTask()
  const [showDone, setShowDone] = useState(false)
  const open = (tasks ?? []).filter((t) => t.status === 'open').sort(byDueDate)
  const done = (tasks ?? []).filter((t) => t.status !== 'open')

  return (
    <Card
      title={`Tasks${open.length ? ` · ${open.length}` : ''}`}
      action={
        <button type="button" onClick={() => onAddingChange(true)} className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-dark">
          <Plus size={13} /> Add task
        </button>
      }
    >
      {open.length === 0 && <p className="text-xs text-gray-400">Nothing to do for this person right now.</p>}
      {open.length > 0 && (
        <div className="-mx-4 -my-1 divide-y divide-gray-100">
          {open.map((t) => (
            <TaskRow key={t.id} task={t} hideContact overdue={!!t.dueAt && isTaskOverdue(t.dueAt)} onComplete={() => complete.mutate(t.id)} />
          ))}
        </div>
      )}
      {done.length > 0 && (
        <button type="button" onClick={() => setShowDone((v) => !v)} className="mt-2 text-xs text-gray-500 hover:text-brand-dark">
          {showDone ? 'Hide' : 'Show'} {done.length} completed
        </button>
      )}
      {showDone && (
        <div className="-mx-4 divide-y divide-gray-100">
          {done.map((t) => <TaskRow key={t.id} task={t} hideContact />)}
        </div>
      )}
      {adding && <NewTaskModal contact={contact} onClose={() => onAddingChange(false)} />}
    </Card>
  )
}
